import { useState, useCallback, useEffect } from 'react';
import { Search, X, Filter } from 'lucide-react';
import clsx from 'clsx';

interface SearchBarProps {
  placeholder?: string;
  onSearch: (query: string) => void;
  className?: string;
  showFilters?: boolean;
  filters?: {
    label: string;
    value: string;
    active: boolean;
  }[];
  onFilterChange?: (value: string) => void;
}

export function SearchBar({
  placeholder = 'Search...',
  onSearch,
  className,
  showFilters = false,
  filters = [],
  onFilterChange,
}: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, onSearch]);

  const handleClear = () => {
    setQuery('');
    onSearch('');
  };

  return (
    <div className={clsx('relative', className)}>
      <div className="relative flex items-center">
        <Search className="absolute left-3 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-10 p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {showFilters && (
          <button
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            className={clsx(
              'absolute right-3 p-1 rounded',
              filters.some((f) => f.active)
                ? 'text-primary-600'
                : 'text-gray-400 hover:text-gray-600'
            )}
          >
            <Filter className="w-4 h-4" />
          </button>
        )}
      </div>

      {showFilterMenu && filters.length > 0 && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-10">
          {filters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => {
                onFilterChange?.(filter.value);
                setShowFilterMenu(false);
              }}
              className={clsx(
                'w-full px-4 py-2 text-left text-sm hover:bg-gray-50',
                filter.active && 'bg-primary-50 text-primary-700'
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
